/* Feb-2026 · Chapter Grade Pill.
 *
 * A tiny badge showing the Flesch-Kincaid grade level of a chapter's
 * content, color-coded against the Canadian secondary target (grade
 * 11-12). Sits next to the chapter title so writers can spot drift
 * without opening the audit panel.
 *
 * All math runs client-side using a compact JS port of the backend's
 * `reading_level.py` heuristic — no network hop per chapter.
 */
import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { BookText } from 'lucide-react';

const VOWELS = /[aeiouy]+/gi;
const WORDS  = /[A-Za-z']+/g;
const SENTS  = /[.!?]+\s+/g;

function syllables(word) {
  const w = word.toLowerCase().replace(/'/g, '');
  if (!w) return 0;
  const groups = w.match(VOWELS) || [];
  let n = Math.max(1, groups.length);
  if (w.endsWith('e') && n > 1 && !w.endsWith('le') && !w.endsWith('ue')) n -= 1;
  return n;
}

function fkGrade(text) {
  if (!text) return 0;
  const wordList = text.match(WORDS) || [];
  if (wordList.length === 0) return 0;
  const words = wordList.length;
  const sents = Math.max(1, text.trim().split(SENTS).filter((s) => s.trim()).length);
  const syll  = wordList.reduce((s, w) => s + syllables(w), 0);
  return Math.round((0.39 * (words / sents) + 11.8 * (syll / words) - 15.59) * 10) / 10;
}

const TARGET_MIN = 11;
const TARGET_MAX = 12;

export default function ChapterGradePill({ text, className, showLabel = false }) {
  const grade = useMemo(() => fkGrade((text || '').slice(0, 6000)), [text]);
  if (!grade || grade <= 0) return null;

  const inBand = grade >= TARGET_MIN - 0.5 && grade <= TARGET_MAX + 0.5;
  const tooLow  = grade < TARGET_MIN - 0.5;
  const tooHigh = grade > TARGET_MAX + 0.5;

  const toneClass = inBand
    ? 'border-success/40 bg-success/10 text-success dark:bg-success/15 dark:text-success/90'
    : tooLow
      ? 'border-warning/40 bg-warning/10 text-warning dark:bg-warning/15 dark:text-warning/90'
      : 'border-destructive/40 bg-destructive/10 text-destructive dark:bg-destructive/15 dark:text-destructive/90';

  const advisory = inBand
    ? `Chapter reads at grade ${grade} — inside the Canadian secondary target (11-12).`
    : tooLow
      ? `Chapter reads at grade ${grade} — below the 11-12 target. Consider richer vocabulary or longer sentences.`
      : `Chapter reads at grade ${grade} — above the 11-12 target. Consider trimming long clauses.`;

  return (
    <span
      title={advisory}
      data-testid="chapter-grade-pill"
      className={cn(
        'inline-flex h-5 items-center gap-1 rounded-sm border px-1.5 font-mono-stat text-[9.5px]',
        toneClass, className,
      )}
    >
      <BookText className="h-2.5 w-2.5" />
      {showLabel && <span className="opacity-70">Gr</span>}
      {grade.toFixed(1)}
    </span>
  );
}
